import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { buyersApi, type BuyerLeadDetailDto } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { Button, Input, Textarea, useToast } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useOptionalAuth } from "@/hooks/useAuth";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";

/**
 * Convert a buyer into a Borrower <c>Party</c>, or link one that already exists.
 *
 * <p>🔴 <b>The copy states what actually carries across, not what the feature was
 * specified to carry.</b> <c>BuyerPartyConversionService.BuildCreateRequest</c>
 * sends first name, last name, email and phone — and nothing else. The address is
 * deliberately absent (a <c>BuyerLead</c> holds only city/state/zip, and
 * <c>AddressDto</c> needs a street line and a country, so populating it would mean
 * inventing the missing parts); the financial figures and the documents are simply
 * not carried. A button that silently under-delivers against its own description
 * is worse than no button, because the specialist finds out weeks later when the
 * borrower record turns out to be empty — so the modal says so before they
 * press it, in the place they would otherwise form the wrong expectation.</p>
 *
 * <p><b>One-way.</b> The service guards the pointer before anything else, so a
 * second attempt is refused rather than minting a second party for one human.
 * There is no un-convert.</p>
 *
 * <p><b>Self-gating on <c>Buyers.Convert</c></b> via <c>useOptionalAuth</c> — the
 * <c>AdminUndoButton</c> precedent — so no caller has to thread a permission flag
 * down, and a page rendered without an <c>AuthProvider</c> (every component test)
 * simply shows nothing rather than throwing.</p>
 */
export function ConvertToBorrowerButton({ buyer }: { buyer: BuyerLeadDetailDto }) {
  const auth = useOptionalAuth();
  const [open, setOpen] = useState(false);

  // No auth context resolved = not permitted. Failing closed is the only safe
  // default for an irreversible identity write.
  if (!auth?.hasPermission("Buyers.Convert")) return null;

  // 🔴 AND the row's ownership, which the server now enforces too (2026-08-02).
  // Conversion follows the module's one ownership rule like every other buyer
  // write: Buyers.ManageAll wins outright, otherwise the assigned specialist,
  // and unassigned is nobody's. `canEdit` is that answer already resolved
  // server-side — never re-derived here, or the two halves of one rule drift.
  //
  // HIDDEN rather than disabled, matching every other affordance on this page:
  // a disabled Convert button on someone else's buyer invites a support ticket
  // asking why it is greyed out, and the answer — "it is not yours" — is
  // already legible from the Specialist field in the header.
  if (!buyer.canEdit) return null;

  if (buyer.convertedPartyId) return null;

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setOpen(true)}
        data-testid="buyer-convert-open"
      >
        Convert to borrower
      </Button>
      <ConvertToBorrowerModal buyer={buyer} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function ConvertToBorrowerModal({
  buyer,
  open,
  onClose,
  onConverted,
}: {
  buyer: BuyerLeadDetailDto;
  open: boolean;
  onClose: () => void;
  /**
   * 🔴 <b>The id of the borrower record this created.</b> It used to be
   * discarded with a literal `void result;` — the one thing the whole
   * irreversible write produces, thrown away on the line after it arrived. The
   * specialist was then told, by this same modal, to go and find that id under
   * Parties.
   *
   * <p>Handed to the host where there is one, and offered on the toast as
   * something to copy where there is not.</p>
   *
   * <p>This used to say there was no route to navigate to. There is —
   * <c>/borrowers/:id</c> — and the buyer header links to it. The claim was
   * written while the header pointed at <c>/parties/{id}</c>, a route the SPA
   * has never registered, so the comment was simultaneously the best evidence
   * that the link was broken and the reason nobody fixed it.</p>
   */
  onConverted?: (partyId: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [mode, setMode] = useState<"mint" | "link">("mint");
  const [email, setEmail] = useState(buyer.email ?? "");
  const [existingPartyId, setExistingPartyId] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  /** The server's own message, rendered in place. Its refusals name the record
   *  holding a taken address and point at the affordance that resolves it, so
   *  re-wording them would throw away the only guidance the specialist has. */
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMode("mint");
    setEmail(buyer.email ?? "");
    setExistingPartyId("");
    setOverrideReason("");
    setError(null);
  }, [open, buyer.email]);

  const convert = useMutation({
    mutationFn: () =>
      buyersApi.convert(buyer.id, {
        existingPartyId: mode === "link" ? existingPartyId.trim() : null,
        // Ignored server-side in link mode; sent as null so the request says
        // what it means rather than carrying a value that does nothing.
        email: mode === "mint" ? trimToNull(email) : null,
        overrideReason: trimToNull(overrideReason),
      }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      onConverted?.(result.partyId);
      toast.success(
        mode === "link" ? "Linked to the borrower record." : "Borrower record created.",
        {
          // The registry has no per-record route, and this modal's own link-mode
          // instructions are "copy the id from the borrower's page" — so the id
          // is exactly what a specialist needs next, and the one thing the write
          // used to throw away.
          action: {
            label: "Copy record id",
            onClick: () => {
              void navigator.clipboard?.writeText(result.partyId).catch(() => {
                /* Clipboard access can be refused outright. A failed copy is not
                   worth an error toast on top of a successful conversion. */
              });
            },
          },
        },
      );
      onClose();
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  const emailChanged = mode === "mint" && trimToNull(email) !== (buyer.email ?? null);
  const blocked =
    convert.isPending ||
    (mode === "link" && existingPartyId.trim().length === 0) ||
    (mode === "mint" && trimToNull(email) === null);

  return (
    <Modal
      open={open}
      onClose={() => (convert.isPending ? undefined : onClose())}
      title="Convert to borrower"
      description="Creates the borrower record this buyer becomes, and links the two permanently."
      size="md"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={onClose} disabled={convert.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              convert.mutate();
            }}
            loading={convert.isPending}
            disabled={blocked}
            data-testid="buyer-convert-submit"
          >
            {convert.isPending
              ? "Converting…"
              : mode === "link"
                ? "Link this borrower"
                : "Create the borrower"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-4">
        {/* --- What actually carries ------------------------------------- */}
        <div
          className="border border-border-light rounded-md bg-white-warm p-space-3"
          data-testid="buyer-convert-carry-notice"
        >
          <p className="text-small text-onyx m-0 mb-space-2">
            <strong>What carries across:</strong> name, email address and phone
            number.
          </p>
          <p className="text-small text-onyx m-0">
            <strong>What does not:</strong> the financial figures on this record
            (liquid capital, net worth, credit band, experience), any uploaded
            documents, and the address — this buyer holds only a city, state and
            ZIP, and a party address needs a street line and a country. Re-enter
            those on the borrower when the PFS arrives. Everything stays on this
            buyer record either way; nothing is moved or deleted.
          </p>
        </div>

        {/* --- Mint or link ---------------------------------------------- */}
        <fieldset className="border-0 p-0 m-0">
          <legend className="text-caption uppercase tracking-wide text-gray-mid mb-space-2">
            How
          </legend>
          <div className="grid gap-space-2">
            <label className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="convert-mode"
                className="mt-[3px]"
                checked={mode === "mint"}
                onChange={() => setMode("mint")}
                data-testid="buyer-convert-mode-mint"
              />
              <span>
                Create a new borrower record
                <span className="block text-caption text-gray-mid">
                  The ordinary case.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="convert-mode"
                className="mt-[3px]"
                checked={mode === "link"}
                onChange={() => setMode("link")}
                data-testid="buyer-convert-mode-link"
              />
              <span>
                Link a borrower record that already exists
                <span className="block text-caption text-gray-mid">
                  When this person is already in the borrowers registry. Nothing
                  is created and nothing on that record is changed.
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {mode === "mint" ? (
          <label className="block">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              Email for the borrower record
            </span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              aria-label="Email for the borrower record"
              data-testid="buyer-convert-email"
              className="mt-space-1"
            />
            <span className="block text-caption text-gray-mid mt-space-1">
              Party email addresses are unique across the whole registry. Correct
              it here if this one is wrong or already taken — the buyer record
              keeps what it has either way.
              {emailChanged && (
                <strong className="block text-onyx mt-space-1">
                  This differs from the buyer’s address on file.
                </strong>
              )}
            </span>
          </label>
        ) : (
          <label className="block">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              Borrower record id
            </span>
            <Input
              value={existingPartyId}
              onChange={(e) => setExistingPartyId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              aria-label="Borrower record id"
              data-testid="buyer-convert-existing-id"
              className="mt-space-1"
            />
            <span className="block text-caption text-gray-mid mt-space-1">
              Copy it from the borrower’s own page under{" "}
              <Link to="/parties" className="text-gold-shadow hover:underline">
                Parties
              </Link>
              . A record that is not a borrower is refused.
            </span>
          </label>
        )}

        <label className="block">
          <span className="text-caption uppercase tracking-wide text-gray-mid">
            Reason <span className="text-gray-mid">(optional)</span>
          </span>
          <Textarea
            rows={2}
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="Why a corrected address or an existing record was chosen."
            aria-label="Reason for the conversion choice"
            data-testid="buyer-convert-reason"
            className="mt-space-1"
          />
        </label>

        <p className="text-caption text-gray-mid m-0">
          Converting sets this buyer’s status to Converted to borrower and cannot
          be undone here — a second attempt is refused rather than creating a
          second record.
        </p>

        {error && (
          <p className="text-small text-status-error m-0" role="alert" data-testid="buyer-convert-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function trimToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
